import type { Simplify } from 'type-fest';
import type { ProjectSources } from '../../typings/enums';
import type { thirdPartyFetchersMap } from '../fetchers';

export enum PackageFilterKeys {
  Fetchers = 'fetchers',
  PackageName = 'packageName',
  Locators = 'locators',
  PackageLocator = 'packageLocator',
}

export enum ProjectFilterKeys {
  Depth = 'depth',
  Labels = 'labels',
  ProjectName = 'projectName',
  ProjectSources = 'sources',
  Visibility = 'visibility',
  BlockTypes = 'blockTypes',
  Teams = 'teamIds',
}

export enum VulnerabilitiesFilterKeys {
  CVE = 'cve',
  CWEs = 'cwes',
  FixTypes = 'fixTypes',
  Severities = 'severities'
}

export type Fetcher = keyof typeof thirdPartyFetchersMap;

export type Depth = 'direct' | 'transitive';
export const Depths: Depth[] = ['direct', 'transitive'];

export type Visibility = 'public' | 'private';
export const Visibilities: Visibility[] = ['public', 'private'];

export type BlockType = 'has_blocked_packages' | 'no_blocked_packages';
export const BlockTypes: BlockType[] = ['has_blocked_packages', 'no_blocked_packages'];

export type FixType = 'has_fix' | 'no_fix';
export const FixTypes: FixType[] = ['has_fix', 'no_fix'];

/**
 * returns whether the hasFix string is valid
 */
export function isValidFixType(fixType: string): fixType is FixType {
  return (FixTypes as string[]).includes(fixType);
}

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'unknown';
export const Severities: Severity[] = ['critical', 'high', 'medium', 'low', 'unknown'];

/**
 * returns whether the severity string is valid
 */
export function isValidSeverity(severity: string): severity is Severity {
  return (Severities as string[]).includes(severity);
}

export type PackageFilter = {
  [PackageFilterKeys.Fetchers]?: Fetcher[];
  [PackageFilterKeys.PackageName]?: string;
  [PackageFilterKeys.Locators]?: string[]; // used for exact matching a list of locators
  [PackageFilterKeys.PackageLocator]?: string; // used for partial matching a single locator
};

export type ProjectFilter = {
  [ProjectFilterKeys.Depth]?: Depth[];
  [ProjectFilterKeys.Labels]?: string[];
  [ProjectFilterKeys.ProjectName]?: string;
  [ProjectFilterKeys.ProjectSources]?: ProjectSources[];
  [ProjectFilterKeys.Visibility]?: Visibility[];
  [ProjectFilterKeys.BlockTypes]?: BlockType[];
}

export type VulnerabilitiesFilter = {
  [VulnerabilitiesFilterKeys.CVE]?: string;
  [VulnerabilitiesFilterKeys.CWEs]?: string[];
  [VulnerabilitiesFilterKeys.FixTypes]?: FixType[];
  [VulnerabilitiesFilterKeys.Severities]?: Severity[]
}

export type TeamsFilter = {
  teamIds?: number[] | 'null';
};

export type AnyFilter = Simplify<PackageFilter & ProjectFilter & VulnerabilitiesFilter & TeamsFilter>;
